import React, { useState, useEffect } from "react";
import "../../../css/ReleaseAppointmentsModal.css";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';


interface Center {
    _id: string;
    center_id: string;
    name: string;
    city: string;
    isActive: boolean;
}

interface SlotTemplate {
    _id: string;
    name: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    center: Center;
}

interface DailyTemplates {
    [key: string]: string;
}

const ReleaseAppointmentsModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, center }) => {
    const [templates, setTemplates] = useState<SlotTemplate[]>([]);
    const [loading, setLoading] = useState(false);

    const [appointmentsPerSlot, setAppointmentsPerSlot] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [dateError, setDateError] = useState("");
    const [dailyTemplates, setDailyTemplates] = useState<DailyTemplates>({
        Monday: "", Tuesday: "", Wednesday: "", Thursday: "",
        Friday: "", Saturday: "", Sunday: "",
    });

    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        if (isOpen) {
            // Reset all form fields when the modal opens
            setAppointmentsPerSlot("");
            setStartDate("");
            setEndDate("");
            setDailyTemplates({
                Monday: "", Tuesday: "", Wednesday: "", Thursday: "",
                Friday: "", Saturday: "", Sunday: "",
            });

            const fetchTemplates = async () => {
                try {
                    const token = sessionStorage.getItem("accessToken");
                    const res = await api.appointment_api.get("/api/slot-templates", {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    const activeTemplates = (res.data || []).filter(
                        (t: any) => !t.disabled_on && !t.disabled
                    );
                    setTemplates(activeTemplates);
                } catch (error) {
                    console.error("Failed to fetch slot templates:", error);
                }
            };
            fetchTemplates();
        }
    }, [isOpen]);

    const handleTemplateChange = (day: string, templateId: string) => {
        setDailyTemplates(prev => ({ ...prev, [day]: templateId }));
    };

    const handleStartDateChange = (value: string) => {
        setStartDate(value);

        if (endDate && value && new Date(endDate) < new Date(value)) {
            setDateError("The end date cannot be before the start date.");
            setEndDate("");
            showToast("The end date cannot be before the start date.", 'error');
            return;
        }

        if (endDate && value && new Date(endDate) >= new Date(value)) {
            setDateError("");
        }
    };

    const handleEndDateChange = (value: string) => {
        if (startDate && new Date(value) < new Date(startDate)) {
            setDateError("The end date cannot be before the start date.");
            setEndDate("");
            showToast("The end date cannot be before the start date.", 'error');
            return;
        }

        setEndDate(value);
        setDateError("");
    };

    const handleAppointmentsPerSlotChange = (value: string) => {
        if (value === "" || value === "3" || value === "5") {
            setAppointmentsPerSlot(value);
            return;
        }

        showToast("Appointments per slot can only be 3 or 5.", 'error');
    };

    const handleSubmit = async () => {
        if (!appointmentsPerSlot || !startDate || !endDate) {
            showToast("Please fill in all required fields.", 'error');
            return;
        }

        if (appointmentsPerSlot !== "3" && appointmentsPerSlot !== "5") {
            showToast("Appointments per slot can only be 3 or 5.", 'error');
            return;
        }

        if (new Date(endDate) < new Date(startDate)) {
            showToast("The end date cannot be before the start date.", 'error');
            return;
        }

        const weekdayTemplateMap: { [key: string]: string } = {};
        for (const day in dailyTemplates) {
            weekdayTemplateMap[day] = dailyTemplates[day] || 'none';
        }

        const payload = {
            center_id: center._id,
            total_appt_per_slot: parseInt(appointmentsPerSlot, 10),
            start_date: startDate,
            end_date: endDate,
            weekday_template_map: weekdayTemplateMap,
        };

        setLoading(true);
        try {
            const token = sessionStorage.getItem("accessToken");
            await api.appointment_api.post("/api/released-slots", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            showToast("Appointments released successfully!", 'success');
            onSuccess?.();
            onClose();
        } catch (err: any) {
            console.error("Failed to release appointments:", err);
            showToast(err.response?.data?.message || "An error occurred. Please try again.", 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content release-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Create Appointments</h2>
                    <button className="global-close-btn" onClick={onClose}>✕</button>
                </div>
                
                <div className="release-form-section">
                    <h3>Center Information</h3>
                    <div className="info-grid">
                        <div>
                            <label style={{paddingBottom: '1rem'}}>Center Name </label>
                            <p>{center.name}</p>
                        </div>
                        <div>
                            <label style={{paddingBottom: '1rem'}}>City</label>
                            <p>{center.city}</p>
                        </div>
                    </div>
                </div>
                
                <div className="release-form-section">
                    <h3>Final Release Details</h3>
                    <div className="final-release">
                            <label htmlFor="appointments" style={{paddingBottom: '5px'}}>Appointments per slot *</label>
                            <select
                                id="appointments"
                                value={appointmentsPerSlot}
                                onChange={(e) => handleAppointmentsPerSlotChange(e.target.value)}
                            >
                                <option value="">Select</option>
                                <option value="3">3</option>
                                <option value="5">5</option>
                            </select>
                        </div>
                    <div className="release-details-grid">
                        
                        <div>
                            <label htmlFor="start-date">Slot Start Date *</label>
                            <input id="start-date" type="date" value={startDate} min={today} onChange={(e) => handleStartDateChange(e.target.value)} />
                        </div>
                        <div>
                            <label htmlFor="end-date">Slot End Date *</label>
                            <input id="end-date" type="date" value={endDate} min={startDate || today} onChange={(e) => handleEndDateChange(e.target.value)} />
                        </div>
                    </div>
                    {dateError && <div style={{ color: '#d32f2f', fontSize: '13px', marginTop: '8px' }}>{dateError}</div>}
                </div>

                <div className="release-form-section">
                    <h3>Select Template</h3>
                    <div className="template-grid">
                        {daysOfWeek.map(day => (
                            <div key={day} className="template-row">
                                <label>{day}</label>
                                <select value={dailyTemplates[day]} onChange={(e) => handleTemplateChange(day, e.target.value)}>
                                    <option value="" disabled>Select a template</option>
                                    {templates.map(template => (
                                        <option key={template._id} value={template._id}>
                                            {template.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn-secondary" onClick={onClose} disabled={loading}>Close</button>
                    <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Creating..." : "Create Appointment"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReleaseAppointmentsModal;